import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import {
  FavoriteList,
  FavoriteListOption,
  OUTLOOK_CATEGORY,
  OUTLOOK_SYNC_FREQUENCY,
  OutlookSyncConfigurationPayload,
  OutlookCategoryOption,
  OutlookSyncHistoryEntry,
  OutlookSyncType as OutlookSyncApiType,
  UpdateSyncConfiguration,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import { OutlookSyncFormGroup } from '../../../form.model';
import { HistoryGridComponent } from '../../components/history-grid/history-grid.component';
import { FavoriteListService } from '@maximizer/core/shared/data-access';
import { Octopus } from '@maximizer/core/shared/domain';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  loadSyncHistory,
  resolveConfigurationId,
} from '../../utils/history-mapper.util';

const FIXED_CATEGORY_OPTIONS: OutlookCategoryOption[] = [
  { id: OUTLOOK_CATEGORY, label: OUTLOOK_CATEGORY },
];

@Component({
  selector: 'maximizer-outlook-contact-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SwitchModule,
    DropDownListModule,
    TranslatePipe,
    TooltipModule,
    HistoryGridComponent,
  ],
  templateUrl: './outlook-contact-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutlookContactTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly outlookSync = inject(UserSyncConfigurationService);
  private readonly favoriteListService = inject(FavoriteListService);

  configuration = input.required<UserSyncConfiguration | null>();
  form = input.required<OutlookSyncFormGroup>();
  payload = input.required<OutlookSyncConfigurationPayload | null>();

  protected readonly syncFrequency = OUTLOOK_SYNC_FREQUENCY.contacts;

  protected readonly favoriteListOptions = signal<FavoriteListOption[]>([]);
  protected readonly outlookCategoryOptions = signal<OutlookCategoryOption[]>(FIXED_CATEGORY_OPTIONS);
  protected readonly history = signal<OutlookSyncHistoryEntry[]>([]);
  protected readonly historyLoading = signal(false);

  protected readonly favoriteListControl = new FormControl<string | null>(null);
  protected readonly outlookCategoryControl = new FormControl<string | null>(
    { value: OUTLOOK_CATEGORY, disabled: true },
  );

  private currentConfigurationId: string | null = null;

  constructor() {
    effect(() => {
      this.configuration();
      this.payload();
      this.handleConfigurationChange();
    });
  }

  ngOnInit(): void {
    this.favoriteListControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => this.setFavoriteListFormValue(id));

    this.form()
      .statusChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncFavoriteListDisabled());
    this.syncFavoriteListDisabled();

    this.setOutlookCategoryFormValue(OUTLOOK_CATEGORY);
  }

  private syncFavoriteListDisabled(): void {
    if (this.form().disabled) {
      this.favoriteListControl.disable({ emitEvent: false });
    } else {
      this.favoriteListControl.enable({ emitEvent: false });
    }
  }

  protected refreshHistory(): void {
    const configurationId = this.getConfigurationId();
    loadSyncHistory({
      service: this.outlookSync,
      destroyRef: this.destroyRef,
      syncType: OutlookSyncApiType.Contacts,
      idPrefix: 'contacts-history',
      errorMessage: 'Failed to load contacts sync history',
      configurationId,
      isCurrent: () => this.currentConfigurationId === configurationId,
      history: this.history,
      loading: this.historyLoading,
    });
  }

  private handleConfigurationChange(): void {
    const configurationId = this.getConfigurationId();

    if (!configurationId) {
      this.currentConfigurationId = null;
      this.resetState();
      return;
    }

    if (configurationId === this.currentConfigurationId) {
      this.syncFavoriteListControl();
      return;
    }

    this.currentConfigurationId = configurationId;
    this.hydrateFavoriteListFromConfiguration(configurationId);
    this.refreshHistory();
    this.syncFavoriteListControl();
  }

  private resetState(): void {
    this.favoriteListOptions.set([]);
    this.history.set([]);
    this.historyLoading.set(false);
    this.favoriteListControl.setValue(null, { emitEvent: false });
    this.setFavoriteListFormValue(null);
  }

  private syncFavoriteListControl(): void {
    const nextValue =
      this.form().controls.favoriteListIds.value?.[0] ??
      this.configuration()?.contactsSync?.favoriteListKey ??
      null;

    if (this.favoriteListControl.value !== nextValue) {
      this.favoriteListControl.setValue(nextValue, { emitEvent: false });
    }
    this.setFavoriteListFormValue(nextValue);
  }

  private hydrateFavoriteListFromConfiguration(configurationId: string): void {
    const key = this.configuration()?.contactsSync?.favoriteListKey ?? null;
    const name = this.configuration()?.contactsSync?.favoriteListName ?? null;

    if (key) {
      const option: FavoriteListOption = {
        id: key,
        label: name ?? key,
        type: 'contact',
      };
      this.favoriteListOptions.set([option]);
      this.syncFavoriteListControl();
      return;
    }

    this.loadFavoriteListsFallback(configurationId);
  }

  private loadFavoriteListsFallback(configurationId: string): void {
    const userUid = this.payload()?.userId ?? '';
    const expectedName = `Outlook Sync (${userUid})`.toLowerCase();

    this.outlookSync
      .getFavoriteLists(configurationId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((lists) => {
          const match = this.mapFavoriteLists(lists ?? []).find(
            (option) => option.label.toLowerCase() === expectedName,
          );
          if (match) {
            this.favoriteListOptions.set([match]);
            this.favoriteListControl.setValue(match.id, { emitEvent: false });
            this.setFavoriteListFormValue(match.id);
            return of(null);
          }
          return this.autoCreateFavoriteList(configurationId);
        }),
        catchError((error) => {
          console.error('Failed to load favorite lists fallback', error);
          this.favoriteListOptions.set([]);
          return of(null);
        }),
      )
      .subscribe();
  }

  private mapFavoriteLists(lists: FavoriteList[]): FavoriteListOption[] {
    return lists.reduce<FavoriteListOption[]>((acc, list) => {
      const key = list.key;
      if (!key) {
        return acc;
      }
      acc.push({ id: key, label: list.name ?? key, type: 'contact' });
      return acc;
    }, []);
  }

  private autoCreateFavoriteList(configurationId: string) {
    const userUid = this.payload()?.userId ?? '';
    const name = `Outlook Sync (${userUid})`;

    return this.favoriteListService.createAddressBookFavoriteList(name, Octopus.EntityType.AbEntry).pipe(
      catchError((error) => {
        console.error('Failed to create favorite list', error);
        return of(null);
      }),
      switchMap((created) => {
        if (!created?.key) {
          return of(null);
        }

        const option: FavoriteListOption = {
          id: created.key,
          label: created.name ?? name,
          type: 'contact',
        };
        this.favoriteListOptions.set([option]);
        this.favoriteListControl.setValue(created.key, { emitEvent: false });
        this.setFavoriteListFormValue(created.key);

        const settings: UpdateSyncConfiguration = {
          maximizerUserUid: userUid,
          contactsSync: {
            enabled: this.form().controls.contactsSyncEnabled.value,
            favoriteListKey: created.key,
            favoriteListName: created.name ?? name,
            outlookCategory: OUTLOOK_CATEGORY,
          },
        };
        return this.outlookSync
          .updateConfiguration(settings, configurationId)
          .pipe(catchError(() => of(null)));
      }),
    );
  }

  private setFavoriteListFormValue(value: string | null): void {
    this.form().controls.favoriteListIds.setValue(value ? [value] : []);
    const match = this.favoriteListOptions().find((opt) => opt.id === value);
    const name =
      match?.label ??
      (value === this.configuration()?.contactsSync?.favoriteListKey
        ? this.configuration()?.contactsSync?.favoriteListName ?? null
        : null);
    this.form().controls.favoriteListName.setValue(name ?? null);
  }

  private setOutlookCategoryFormValue(value: string | null): void {
    this.form().controls.outlookCategoryIds.setValue(value ? [value] : []);
  }

  private getConfigurationId(): string | null {
    return resolveConfigurationId(this.configuration(), this.payload());
  }
}
